import { useEffect, useRef, useState } from 'react'

// BootSequence — full-screen app intro, ported from the studio `boot-sequence`
// component (Atlas Pattern Catalogue, Vol. III). Choreographed in four beats:
//   1. Wordmark letter-by-letter
//   2. Three rings expand from center
//   3. Dot globe zooms in
//   4. Dot globe zooms back out
// then auto-fades into the app. Shows once per browser session (see App.jsx
// sessionStorage gate). Honors prefers-reduced-motion by skipping straight to
// onDone. The studio original is a click-to-replay card; here it's a one-shot
// fullscreen overlay with an onDone callback instead.

const WORDMARK = 'ATLAS'

export default function BootSequence({ onDone }) {
  const [step, setStep] = useState(0)
  const [leaving, setLeaving] = useState(false)
  const timeouts = useRef([])

  useEffect(() => {
    const reduce = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (reduce) { onDone?.(); return }
    const t = timeouts.current
    t.push(setTimeout(() => setStep(1), 700))        // rings
    t.push(setTimeout(() => setStep(2), 1500))       // globe zooms in
    t.push(setTimeout(() => setStep(3), 2700))       // globe zooms back out
    t.push(setTimeout(() => setLeaving(true), 3000)) // begin fade
    t.push(setTimeout(() => onDone?.(), 3550))       // unmount
    return () => t.forEach(clearTimeout)
  }, [onDone])

  // Size to the viewport so the intro reads BIG on a phone (was a fixed small
  // card scale). vmin captured once on mount.
  const vmin = typeof window !== 'undefined' ? Math.min(window.innerWidth, window.innerHeight) : 380
  const scale = Math.min(vmin / 210, 2.1)
  const w = vmin * 0.96
  const h = vmin * 0.96

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'hsl(var(--studio-background))',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: leaving ? 0 : 1,
        transition: 'opacity 520ms ease',
        pointerEvents: leaving ? 'none' : 'auto',
      }}
    >
      {/* internal stage — fixed proportions so the choreography reads the same
          at any viewport; the rings/globe pin to this stage's center */}
      <div style={{ position: 'relative', width: w, height: h, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {/* Step 1: wordmark letter-by-letter */}
        {step >= 0 && step < 2 && (
          <div
            style={{
              fontFamily: 'var(--studio-font-display)',
              fontStyle: 'italic',
              fontSize: 56 * scale,
              color: 'hsl(var(--studio-accent-soft))',
              letterSpacing: '-0.02em',
              lineHeight: 1,
            }}
          >
            {WORDMARK.split('').map((c, i) => (
              <span
                key={i}
                style={{
                  display: 'inline-block',
                  animation: `boot-letter 380ms cubic-bezier(0.2, 0.8, 0.2, 1) ${i * 80}ms both`,
                }}
              >
                {c}
              </span>
            ))}
          </div>
        )}

        {/* Step 2: rings expand */}
        {step >= 1 && step < 3 && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {[0, 1, 2].map((i) => (
              <span
                key={`ring-${i}`}
                style={{
                  position: 'absolute',
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  border: '1px solid hsl(var(--studio-accent))',
                  animation: `boot-ring 1100ms ease-out ${i * 220}ms both`,
                }}
              />
            ))}
          </div>
        )}

        {/* Steps 3-4: dot globe zooms in, holds, then zooms back out */}
        {step >= 2 && (
          <div
            style={{
              opacity: step >= 3 ? 0 : 1,
              transform: `scale(${step >= 3 ? 0.4 : 1})`,
              transition: 'opacity 600ms ease, transform 600ms cubic-bezier(0.4, 0, 0.8, 1)',
              animation: 'boot-globe 700ms cubic-bezier(0.2, 0.8, 0.2, 1)',
            }}
          >
            <Globe size={Math.min(w, h) * 0.72} />
          </div>
        )}
      </div>

      <style>{`
        @keyframes boot-letter { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes boot-ring { from { transform: scale(0); opacity: 0.9; } to { transform: scale(8); opacity: 0; } }
        @keyframes boot-globe { from { transform: scale(0.4); opacity: 0; } to { transform: scale(1); opacity: 1; } }
      `}</style>
    </div>
  )
}

function Globe({ size }) {
  const N = 120
  const off = 2 / N
  const inc = Math.PI * (3 - Math.sqrt(5))
  const tilt = (23.5 * Math.PI) / 180
  const pts = []
  for (let i = 0; i < N; i++) {
    const y = i * off - 1 + off / 2
    const r = Math.sqrt(1 - y * y)
    const phi = i * inc
    pts.push({ x: Math.cos(phi) * r, y, z: Math.sin(phi) * r })
  }
  const R = size / 2
  const cosT = Math.cos(tilt)
  const sinT = Math.sin(tilt)

  return (
    <svg width={size} height={size} viewBox={`-${R} -${R} ${size} ${size}`}>
      {pts.map((p, i) => {
        const y2 = p.y * cosT - p.z * sinT
        const z2 = p.y * sinT + p.z * cosT
        if (z2 < -0.05) return null
        const d = (z2 + 1) / 2
        return <circle key={i} cx={p.x * R * 0.94} cy={y2 * R * 0.94} r={0.4 + d * 1.2} fill="hsl(var(--studio-accent-soft))" opacity={0.2 + d * 0.7} />
      })}
    </svg>
  )
}
